import {
  DOMParser as XMLDOMParser,
  XMLSerializer,
} from "@xmldom/xmldom";
import type {
  ImportedChecklist,
  ImportedChecklistCell,
  ImportedChecklistRow,
  ImportedChecklistTable,
} from "~/types/ImportedChecklist";
import { hasWritingCompanionChecklistSection } from "~/utils/writing-companion-checklist.utils";

const CHECKLIST_TITLE_PATTERN = /\b(?:Composition|Practicum) Checklist\b/i;

type ChecklistExtractionResult = {
  html: string;
  importedChecklist?: ImportedChecklist;
};

const normalizeText = (value: string | null | undefined) =>
  value?.replace(/\s+/g, " ").trim() ?? "";

const getElementChildren = (element: Element) =>
  Array.from(element.childNodes).filter(
    (node): node is Element => node.nodeType === 1
  );

const getDirectRows = (table: Element) => {
  const rows: Element[] = [];

  for (const child of getElementChildren(table)) {
    if (child.tagName.toLowerCase() === "tr") {
      rows.push(child);
      continue;
    }

    if (["thead", "tbody", "tfoot"].includes(child.tagName.toLowerCase())) {
      rows.push(
        ...getElementChildren(child).filter(
          (row) => row.tagName.toLowerCase() === "tr"
        )
      );
    }
  }

  return rows;
};

const getDirectCells = (row: Element) =>
  getElementChildren(row).filter(
    (cell) =>
      cell.tagName.toLowerCase() === "td" ||
      cell.tagName.toLowerCase() === "th"
  );

const getClosestTable = (node: Node | null) => {
  let current = node;

  while (current) {
    if (
      current.nodeType === 1 &&
      (current as Element).tagName.toLowerCase() === "table"
    ) {
      return current as Element;
    }
    current = current.parentNode;
  }

  return undefined;
};

const getDirectNestedTables = (cell: Element) => {
  const outerTable = getClosestTable(cell);

  return Array.from(cell.getElementsByTagName("table")).filter(
    (table) => getClosestTable(table.parentNode) === outerTable
  ) as unknown as Element[];
};

const getCellText = (cell: Element) => {
  const clone = cell.cloneNode(true) as Element;
  Array.from(clone.getElementsByTagName("table")).forEach((table) =>
    table.parentNode?.removeChild(table)
  );
  return normalizeText(clone.textContent);
};

const parseSpan = (cell: Element, attributeName: string) => {
  const value = Number.parseInt(cell.getAttribute(attributeName) ?? "", 10);
  return Number.isFinite(value) && value > 0 ? value : 1;
};

const parseCell = (cell: Element): ImportedChecklistCell => ({
  text: getCellText(cell),
  colSpan: parseSpan(cell, "colspan"),
  rowSpan: parseSpan(cell, "rowspan"),
  tables: getDirectNestedTables(cell).map(parseTable),
});

const parseRow = (row: Element): ImportedChecklistRow => ({
  cells: getDirectCells(row).map(parseCell),
});

function parseTable(table: Element): ImportedChecklistTable {
  return {
    rows: getDirectRows(table).map(parseRow),
  };
}

const extractFromHtml = (html: string): ChecklistExtractionResult => {
  const document = new XMLDOMParser({
    errorHandler: {
      warning: () => undefined,
      error: () => undefined,
      fatalError: () => undefined,
    },
  }).parseFromString(
    `<root>${html}</root>`,
    "text/xml"
  );
  const container = document.documentElement as unknown as Element;

  if (!container || container.tagName.toLowerCase() !== "root") return { html };

  const children = getElementChildren(container);
  const tableIndex = children.findIndex(
    (element) => element.tagName.toLowerCase() === "table"
  );

  if (tableIndex <= 0) return { html };

  const table = children[tableIndex];
  const headingElements = children.slice(0, tableIndex);
  const headingLines = headingElements
    .map((element) => normalizeText(element.textContent))
    .filter(Boolean);

  if (!headingLines.some((line) => CHECKLIST_TITLE_PATTERN.test(line))) {
    return { html };
  }

  const parsedTable = parseTable(table);
  if (
    parsedTable.rows.length === 0 ||
    parsedTable.rows.every((row) => row.cells.length === 0)
  ) {
    return { html };
  }

  const serializer = new XMLSerializer();
  // Use HTML serialization: a self-closing page-break div would swallow all
  // following paragraphs when the browser parses it for the clipboard.
  const remainingHtml = children
    .slice(tableIndex + 1)
    .map((element) => serializer.serializeToString(element, true))
    .join("");

  return {
    html: remainingHtml,
    importedChecklist: {
      source: "writing-companion-docx",
      headingLines,
      table: parsedTable,
    },
  };
};

export const useExtractWritingCompanionChecklist = ({
  documentXml,
  html,
}: {
  documentXml: string;
  html: string;
}): ChecklistExtractionResult => {
  try {
    if (!hasWritingCompanionChecklistSection(documentXml)) {
      return { html };
    }

    return extractFromHtml(html);
  } catch (error) {
    console.warn("Failed to extract Writing Companion checklist:", error);
    return { html };
  }
};
